/**
 * Catching the server up, and catching up with the server.
 *
 * Always in that order. Pushing first means the outbox is empty by the time anything is read
 * back, so a refresh cannot overwrite a change the user has made but this device has not yet
 * sent. Getting that ordering wrong would lose work silently, which is the one failure this
 * whole design exists to prevent.
 */

import type { ApiClient } from '../api/client';
import type { RejectedMutation } from '../api/types';
import { META, readMeta, writeMeta, type Database } from '../store/database';
import { replaceEverything } from '../store/entities';
import { asMutations, forget, pending } from '../store/outbox';
import { replay } from './events';

export interface SyncOutcome {
  /** Mutations the server accepted. */
  pushed: number;
  /**
   * Mutations the server understood and refused — a change to something another device
   * deleted, say. They are dropped from the queue, because leaving them would wedge it, so
   * the caller has to tell the user: this is the one path where work is discarded.
   */
  rejected: RejectedMutation[];
  /** Events read back from the log. */
  pulled: number;
  /** Whether the local copy had to be re-read in full. */
  refreshed: boolean;
}

export class SyncEngine {
  constructor(
    private readonly db: Database,
    private readonly client: ApiClient,
  ) {}

  /** One full cycle. Throws if the API cannot be reached, leaving the queue intact. */
  async run(): Promise<SyncOutcome> {
    const { pushed, rejected } = await this.push();
    const { pulled, refreshed } = await this.pull();

    return { pushed, rejected, pulled, refreshed };
  }

  /** Reads the whole household back from the server, replacing what is here. */
  async refresh(): Promise<void> {
    const [items, locations, groceries, shopping] = await Promise.all([
      this.client.inventory(),
      this.client.locations(),
      this.client.groceries(),
      this.client.shopping(),
    ]);

    await replaceEverything(this.db, { items, locations, groceries, shopping });
    await writeMeta(this.db, META.refreshedAt, new Date().toISOString());
  }

  private async push(): Promise<Pick<SyncOutcome, 'pushed' | 'rejected'>> {
    const queued = await pending(this.db);
    if (queued.length === 0) {
      return { pushed: 0, rejected: [] };
    }

    const receipt = await this.client.push(asMutations(queued));

    // Everything the server accounted for leaves the queue, accepted or refused alike. A
    // refused mutation that stayed would be retried forever and nothing behind it would ever
    // be sent.
    await forget(this.db, [
      ...receipt.applied.map((applied) => applied.key),
      ...receipt.rejected.map((rejected) => rejected.key),
    ]);

    await this.rememberOwn(receipt.applied.map((applied) => applied.seq));

    return { pushed: receipt.applied.length, rejected: receipt.rejected };
  }

  private async pull(): Promise<Pick<SyncOutcome, 'pulled' | 'refreshed'>> {
    const since = await readMeta(this.db, META.syncedSeq, 0);
    const delta = await this.client.delta(since);
    const own = new Set(await readMeta<number[]>(this.db, META.ownSeqs, []));

    // A device that has never read the household cannot arrive at a correct copy from the log
    // alone: the log carries change, and this device has nothing for that change to apply to.
    // A household seeded before its first event has no events at all, so without this its
    // locations never arrive and nothing can be added anywhere.
    let staleness = !(await this.hasReadTheHousehold());

    for (const event of delta.events) {
      // This device applied its own changes optimistically before sending them, so replaying
      // them would be work at best and a visible stutter at worst.
      if (own.has(event.seq)) {
        own.delete(event.seq);
        continue;
      }

      if ((await replay(this.db, event)) === 'needs-refresh') {
        staleness = true;
      }
    }

    // Only ever with an empty outbox: `run` pushes first, and a failed push throws before
    // reaching here.
    const refreshed = staleness && (await pending(this.db)).length === 0;
    if (refreshed) {
      await this.refresh();
    }

    await writeMeta(this.db, META.syncedSeq, delta.seq);
    await writeMeta(this.db, META.ownSeqs, [...own]);

    return { pulled: delta.events.length, refreshed };
  }

  private async hasReadTheHousehold(): Promise<boolean> {
    return (await readMeta<string | null>(this.db, META.refreshedAt, null)) !== null;
  }

  private async rememberOwn(seqs: number[]): Promise<void> {
    const known = await readMeta<number[]>(this.db, META.ownSeqs, []);

    await writeMeta(this.db, META.ownSeqs, [...known, ...seqs]);
  }
}
